import { useEffect, useRef } from "react";
import { Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { SettingsNav } from "../../../components/settings-nav";
import { setActiveServerId } from "../../../lib/activeServer";
import { useScrollFade } from "../../../lib/useScrollFade";

/**
 * Settings layout — the "one sheet" design: the authed shell's blurred still
 * (components/page-backdrop, mounted ONCE in routes/_authed for every page,
 * so it never reloads between here and Profile) behind a rail
 * of chip rows (SettingsNav) and ONE frosted sheet
 * that every page fills with hairline-separated blocks
 * (components/settings-sheet.tsx). The window scrolls; rail and sheet
 * both ride under the floating bar, dissolving just beneath it like the
 * detail pages (.rago-scroll-fade + lib/useScrollFade on the shell — the
 * backdrop lives in the authed layout, outside the shell, so the mask never
 * cuts the art), and
 * the sheet runs under the viewport's bottom edge. One width for every
 * page — the sheet is the column. The materials and the rail/sheet grid
 * live in styles/app.css (.rago-settings-*, .rago-sheet).
 *
 * A server page is one server's. Every switcher follows to the SAME page
 * for the server picked (lib/serverPick), which would leave the page's
 * instance standing — its draft, field errors, live rows, timers, typed
 * text — with the old server's state under the new server's heading (the
 * "Unsaved changes" pill lit with nothing typed; Kenny, 2026-10-09). So the
 * Outlet is keyed by the server: the page for the new server starts over,
 * as Plex rebuilds its settings view, and unsaved edits go with the switch
 * — no prompt. Account pages have no serverId, and so no key.
 */
export const Route = createFileRoute("/_authed/settings")({
  component: SettingsLayout,
});

function SettingsLayout() {
  const params = useParams({ strict: false }) as { serverId?: string };
  const shellRef = useRef<HTMLDivElement>(null);
  useScrollFade(shellRef);

  // URL wins: deep-linking into a server's settings makes it the active
  // server, so the bar's chip, Home, and Library agree with what you're
  // editing. setActiveServerId no-ops on the same id (StrictMode-safe).
  useEffect(() => {
    if (params.serverId) setActiveServerId(params.serverId);
  }, [params.serverId]);

  return (
    <div ref={shellRef} className="rago-settings-shell rago-scroll-fade">
      <div className="rago-settings-grid">
        <SettingsNav />
        <div className="rago-sheet">
          <Outlet key={params.serverId} />
        </div>
      </div>
    </div>
  );
}
